/**
 * QMS Followers Component
 * Displays and manages the list of users following a QMS ticket.
 * Self follow/unfollow is backend-driven via /v4/qmstickets/:id/followers/me.
 * Management controls (add, remove, self-toggle) require `qmstickets.edit`.
 */

import { useState } from 'react';
import { X, UserPlus, UserMinus } from 'lucide-react';
import { useQmsUserById } from '../../hooks/useQmsUsers';
import { useQmsPermissions } from '../../hooks/useQmsPermissions';
import {
  useAddQmsFollower,
  useRemoveQmsFollower,
  useFollowSelf,
  useUnfollowSelf,
} from '../../hooks/useQmsFollowers';
import { QmsUserSelect } from './QmsUserSelect';
import type { QmsUser } from '../../types/qms.types';

// ---------------------------------------------------------------------------
// Local display helpers (copied from QmsUserSelect to avoid coupling)
// ---------------------------------------------------------------------------

type UserLike = { firstname?: string; lastname?: string; email?: string };

function getUserInitials(user: UserLike): string {
  const first = user.firstname?.charAt(0) || '';
  const last = user.lastname?.charAt(0) || '';
  return (first + last).toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?';
}

function getUserDisplayName(user: UserLike): string {
  if (user.firstname || user.lastname) {
    return `${user.firstname || ''} ${user.lastname || ''}`.trim();
  }
  return user.email || 'Unknown User';
}

// ---------------------------------------------------------------------------
// Follower chip — fetches user display data by Mongo ID
// ---------------------------------------------------------------------------

interface FollowerChipProps {
  userId: string;
  onRemove: (userId: string) => void;
  removeDisabled: boolean;
  canManage: boolean;
}

function FollowerChip({ userId, onRemove, removeDisabled, canManage }: FollowerChipProps) {
  const { data: user } = useQmsUserById(userId);

  const displayName = user ? getUserDisplayName(user) : userId.slice(-6);
  const initials = user ? getUserInitials(user) : '?';

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-atek-blue/10 px-3 py-1 text-sm text-atek-blue">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-atek-blue text-[10px] font-medium text-white">
        {initials}
      </span>
      <span>{displayName}</span>
      {canManage && (
        <button
          type="button"
          aria-label={`Remove follower ${displayName}`}
          onClick={() => onRemove(userId)}
          disabled={removeDisabled}
          className="ml-1 rounded-full p-0.5 hover:bg-atek-blue/20 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-atek-blue"
        >
          <X aria-hidden="true" className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// QmsFollowers
// ---------------------------------------------------------------------------

interface QmsFollowersProps {
  ticketId: string;
  followers: string[];
  /** Backend-computed: true when the currently authenticated viewer is already following. */
  viewerIsFollower: boolean;
}

export function QmsFollowers({ ticketId, followers, viewerIsFollower }: QmsFollowersProps) {
  // canEditTickets mirrors the backend requireQmsTicketsAccess gate on all follower mutations
  const { canEditTickets } = useQmsPermissions();
  const canManage = canEditTickets;

  const addFollower = useAddQmsFollower(ticketId);
  const removeFollower = useRemoveQmsFollower(ticketId);
  const followSelfMutation = useFollowSelf(ticketId);
  const unfollowSelfMutation = useUnfollowSelf(ticketId);

  const [selectedUser, setSelectedUser] = useState<QmsUser | undefined>(undefined);

  // Concurrency lock: disable all controls while any mutation is in-flight
  const anyPending =
    addFollower.isPending ||
    removeFollower.isPending ||
    followSelfMutation.isPending ||
    unfollowSelfMutation.isPending;

  // Filter out falsy IDs before rendering chips
  const validFollowers = followers.filter(Boolean);

  const handleAddUser = (u: QmsUser | undefined) => {
    if (!u) return;
    // Guard: no-op if already a follower
    if (validFollowers.includes(u._id)) {
      setSelectedUser(undefined);
      return;
    }
    addFollower.mutate(u._id);
    setSelectedUser(undefined);
  };

  const handleSelfToggle = () => {
    if (viewerIsFollower) {
      unfollowSelfMutation.mutate();
    } else {
      followSelfMutation.mutate();
    }
  };

  return (
    <div className="border-t border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Followers</h3>

        {/* Self follow/unfollow — only editors can mutate; state is backend-driven */}
        {canManage && (
          <button
            type="button"
            aria-label={viewerIsFollower ? 'Unfollow this ticket' : 'Follow this ticket'}
            onClick={handleSelfToggle}
            disabled={anyPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-atek-blue"
          >
            {viewerIsFollower ? (
              <>
                <UserMinus aria-hidden="true" className="h-4 w-4" />
                Unfollow
              </>
            ) : (
              <>
                <UserPlus aria-hidden="true" className="h-4 w-4" />
                Follow
              </>
            )}
          </button>
        )}
      </div>

      {/* Follower chips — always visible regardless of edit permission */}
      {validFollowers.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-4">
          {validFollowers.map((id) => (
            <FollowerChip
              key={id}
              userId={id}
              onRemove={(uid) => removeFollower.mutate(uid)}
              removeDisabled={anyPending}
              canManage={canManage}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-4">No followers yet.</p>
      )}

      {/* Add follower picker — only shown to editors; ariaLabel replaces broken htmlFor pattern */}
      {canManage && (
        <div className="max-w-xs">
          <QmsUserSelect
            value={selectedUser}
            onChange={handleAddUser}
            placeholder="Add follower…"
            ariaLabel="Add follower"
            disabled={anyPending}
          />
        </div>
      )}
    </div>
  );
}
